// conception.md §5: one visible field, "add another page" below it, role placeholders instead
// of explanatory labels, consent checkbox. Client-side domain matching here is a UX nicety only
// (a naive last-two-labels heuristic — wrong for multi-part TLDs like .co.uk) — the server
// re-validates authoritatively via a real public-suffix list (architecture.md §3.1), so a
// false negative here just means a slightly less helpful inline message, never a security gap.
import type { ScanUrlRole } from "./types.js";

const ROLE_PLACEHOLDERS: { role: ScanUrlRole; placeholder: string; hint?: string }[] = [
  { role: "home", placeholder: "Homepage           https://…" },
  { role: "article", placeholder: "Article / Post     https://…", hint: "usually the most important" },
  { role: "category", placeholder: "Category / Archive https://…" },
  { role: "product", placeholder: "Product / Shop     https://…" },
];

// UX fix (user-reported): typing a bare domain like "seo-head.de" (no scheme) failed validation
// with "doesn't look like a valid URL" — technically correct (the WHATWG URL parser requires a
// scheme), but not what anyone expects to have to type. Auto-prepend https:// when there's no
// recognizable scheme already, so the common case ("just type your domain") works without
// forcing the user to know or care that a URL technically needs "https://" in front of it.
function normalizeUserTypedUrl(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return trimmed;
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function naiveRegistrableDomain(rawUrl: string): string | null {
  try {
    const hostname = new URL(rawUrl).hostname;
    const parts = hostname.split(".");
    return parts.length <= 2 ? hostname : parts.slice(-2).join(".");
  } catch {
    return null;
  }
}

function escapeAttr(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML.replace(/"/g, "&quot;");
}

export interface FormSubmitData {
  urls: { url: string; role: ScanUrlRole }[];
  consentChecked: boolean;
}

export interface FormHandlers {
  onSubmit(data: FormSubmitData): void;
}

export class FormRenderer {
  private container: HTMLElement;
  private handlers: FormHandlers;
  private fieldCount = 1;
  private savedValues: string[] = [];
  private savedConsentChecked = false;

  constructor(container: HTMLElement, handlers: FormHandlers) {
    this.container = container;
    this.handlers = handlers;
  }

  /**
   * Reads whatever's currently in the DOM before render() replaces it via innerHTML. This is
   * what makes render() safe to call repeatedly (clicking "Add another page", or a validation
   * error re-rendering the whole widget) without silently discarding what the user already
   * typed — a bug found live: every re-render used to rebuild the form from empty defaults.
   * Reading from the DOM (not just an internal field) means this works even when a brand new
   * FormRenderer instance renders into the same container, which is exactly what happens on a
   * validation-error re-render (AdPolicyCheckerElement.render() constructs a fresh one).
   */
  private captureCurrentDomState(): void {
    const inputs = Array.from(this.container.querySelectorAll<HTMLInputElement>(".apc-url-input"));
    if (inputs.length === 0) return; // nothing rendered yet — keep defaults (1 empty field)
    this.savedValues = inputs.map((input) => input.value);
    this.fieldCount = Math.max(1, inputs.length);
    const consentEl = this.container.querySelector<HTMLInputElement>(".apc-consent-checkbox");
    if (consentEl) this.savedConsentChecked = consentEl.checked;
  }

  render(validationError: string | null): void {
    this.captureCurrentDomState();
    this.renderFromState(validationError);
  }

  private renderFromState(validationError: string | null): void {
    const rows = Array.from({ length: this.fieldCount }, (_, i) => {
      const cfg = ROLE_PLACEHOLDERS[i] ?? ROLE_PLACEHOLDERS[ROLE_PLACEHOLDERS.length - 1]!;
      const savedValue = this.savedValues[i] ?? "";
      return `
        <div class="apc-form-row">
          <input type="url" class="apc-url-input" data-role="${cfg.role}" placeholder="${cfg.placeholder}" value="${escapeAttr(savedValue)}" ${i === 0 ? "required" : ""} />
        </div>
      `;
    }).join("");

    this.container.innerHTML = `
      <form class="apc-form" novalidate>
        <p class="apc-form-intro">Ad placements differ by page type. The more different pages you provide, the more complete the report.</p>
        ${rows}
        ${this.fieldCount < ROLE_PLACEHOLDERS.length ? `<button type="button" class="apc-add-field">+ Add another page</button>` : ""}
        <label class="apc-consent-row">
          <input type="checkbox" class="apc-consent-checkbox" ${this.savedConsentChecked ? "checked" : ""} />
          I'm authorized to have this domain scanned.
        </label>
        <div class="apc-turnstile-container"></div>
        ${validationError ? `<p class="apc-validation-error" role="alert">${validationError}</p>` : ""}
        <button type="submit" class="apc-submit-button">Check my site</button>
      </form>
    `;

    const form = this.container.querySelector("form")!;
    const addButton = this.container.querySelector(".apc-add-field");
    addButton?.addEventListener("click", () => {
      // Deliberately capture-then-increment-then-render-without-recapturing: render()'s own
      // capture step would otherwise read the count straight back off the (still old, not yet
      // rebuilt) DOM and silently overwrite the +1 we're trying to apply here.
      this.captureCurrentDomState();
      this.fieldCount = Math.min(this.fieldCount + 1, ROLE_PLACEHOLDERS.length);
      this.renderFromState(null);
    });

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const inputs = Array.from(this.container.querySelectorAll<HTMLInputElement>(".apc-url-input"));
      const urls = inputs
        .map((input) => ({ url: normalizeUserTypedUrl(input.value), role: input.dataset.role as ScanUrlRole }))
        .filter((entry) => entry.url.length > 0);
      const consentChecked = this.container.querySelector<HTMLInputElement>(".apc-consent-checkbox")!.checked;
      this.handlers.onSubmit({ urls, consentChecked });
    });
  }

  get turnstileContainer(): HTMLElement {
    return this.container.querySelector(".apc-turnstile-container")!;
  }
}

/** prd.md §2.1 S1: all URLs must share a registrable domain (client-side pre-check only). */
export function validateSameDomain(urls: string[]): string | null {
  if (urls.length === 0) return "Please enter at least one URL.";
  for (const url of urls) {
    try {
      new URL(url);
    } catch {
      return `"${url}" doesn't look like a valid URL.`;
    }
  }
  const domains = urls.map(naiveRegistrableDomain);
  const first = domains[0];
  if (!first || domains.some((d) => d !== first)) {
    return "These URLs belong to different domains — please only enter pages from the same website.";
  }
  return null;
}
